import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  DialogBody,
  Button,
} from '@fluentui/react-components';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getSessionStorage } from '@bingads-webui/storage';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SummaryBarContainer } from '@bingads-webui-react/summary-bar';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';
import { ActionButton } from '@bingads-webui-react/fabric-control';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { getAdStudioSummaryBarStyle } from './styles/ad-studio-summary-bar-style';
import { SimpleBreadcrumb } from './components/simple-breadcrumb';

const SessionStorage = getSessionStorage();
const SESSION_KEY = 'ASSET_LIBRARY_STORAGE';
const QUERY_INTERVAL = 1 * 1000;
const MAX_QUERY_COUNT = (30 * 1000) / QUERY_INTERVAL; // query few more seconds after count is 0

const AdStudioSummaryBarInternal = ({
  selectedParent,
  selectedChild,
  location,
  permissions,
  i18n,
  classes,
  scope,
} = {}) => {
  const [inProgressTaskCount, setInProgressTaskCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  // query in progress tasks count
  useEffect(() => {
    if (!selectedParent || selectedParent.id !== 'ad-studio-app-entry') {
      return null;
    }
    let counter = 0;
    let timerId = setInterval(() => {
      let { inProgressTaskCount: val } = SessionStorage.get(SESSION_KEY) || {};
      val = val || 0;
      setInProgressTaskCount(val);

      // stop query after task count becomes 0 for a while
      counter = val > 0 ? 0 : counter + 1;
      if (!val && counter > MAX_QUERY_COUNT) {
        clearInterval(timerId);
        timerId = undefined;
      }
    }, QUERY_INTERVAL);
    return () => {
      if (timerId !== undefined) {
        clearInterval(timerId);
      }
    };
  }, [selectedParent]);

  const onExit = useCallback(() => {
    setDialogOpen(false);
    const previousRoute = location.state && location.state.previousRoute;
    const url = previousRoute ? `${new URL(previousRoute).pathname}` : `${selectedParent.routePrefix}${selectedParent.route}`;
    scope.remove(['templateGroupId', 'templateId']);
    scope.save(url);
  }, [scope, selectedParent, location]);

  const onCancelDialog = useCallback(() => {
    document.querySelector('.ui-next-summary-bar .back-button').focus();
  }, []);

  const backButtonProps = useMemo(() => {
    if (location && selectedParent && selectedChild
        && (selectedChild.id === 'ad-studio-generate-image-page' ||
          selectedChild.id === 'ad-studio-edit-image-page' ||
          selectedChild.id === 'ad-studio-display-template-create-page' ||
          selectedChild.id === 'ad-studio-display-template-edit-page' ||
          selectedChild.id === 'ad-studio-video-template-page' ||
          selectedChild.id === 'ad-studio-video-create-page' ||
          selectedChild.id === 'ad-studio-video-edit-page'
        )) {
      let previousRoute = location.state && location.state.previousRoute;

      let onClick;
      const isTemplateCreationEditPage = selectedChild.id === 'ad-studio-display-template-create-page' ||
        selectedChild.id === 'ad-studio-display-template-edit-page' ||
        selectedChild.id === 'ad-studio-video-edit-page' ||
        selectedChild.id === 'ad-studio-video-create-page';
      if (isTemplateCreationEditPage) {
        onClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          setDialogOpen(true);
        };
      }

      if (selectedChild.id === 'ad-studio-video-template-page' && previousRoute && previousRoute.indexOf('/video/create?') > -1) {
        previousRoute = previousRoute.replace('/video/create?', '/videos?');
      }

      return {
        url: previousRoute ? `${new URL(previousRoute).pathname}${location.search}` : `${selectedParent.routePrefix}${selectedParent.route}${location.search}`,
        buttonText: i18n.getString(_TL_('Back')),
        ...(onClick ? { onClick } : {}),
      };
    }
    return null;
  }, [i18n, location, selectedChild, selectedParent, setDialogOpen]);

  // MAE needs the ability to hide back buttons that lead outside of Ads Studio
  // This will prevent users from navigating to the campaign/account pages
  const hideBackButton = useMemo(() => {
    if (location) {
      const urlParams = new URLSearchParams(location.search);
      return urlParams.get('applicationName') === 'BingAdsEditor' && backButtonProps === null;
    }
    return false;
  }, [location, backButtonProps]);

  const renderInProgressContent = useCallback(() => {
    // only show when there are in progress tasks
    if (inProgressTaskCount) {
      return (
        <div className={classNames('summary-bar-items', classes.inProgressContent)}>
          <ActionButton
            className="in-progress-button"
            iconProps={{ iconName: 'Sync' }}
            onClick={() => {
              // history.push(`${selectedParent.routePrefix}assetlibrary/taskprogress${location.search}`);
            }}
          >
            {inProgressTaskCount > 1
              ? i18n.getString(_TL_('Updating {{count}} items'), { count: inProgressTaskCount })
              : i18n.getString(_TL_('Updating 1 item'))}
          </ActionButton>
        </div>
      );
    }
    return null;
  }, [classes, i18n, inProgressTaskCount]);

  const summaryBar = useMemo(() => (
    <div className={classes.adStudioSummaryBar}>
      <div id="ad-studio-summary-bar-breadcrumb" className="breadcrumb-wrapper">
        <SimpleBreadcrumb
          selectedItem={selectedParent}
          maxDisplayedItems={3}
          i18n={i18n}
          showTag={false}
          tagClass=""
        />
      </div>
      <div id="ad-studio-summary-bar-customize-container">
        {/* render customized content here using React.createPortal */}
      </div>
      {
        renderInProgressContent()
      }
    </div>
  ), [classes.adStudioSummaryBar, i18n, renderInProgressContent, selectedParent]);

  const ariaLabel = useMemo(() => ({
    ariaLabel: i18n.getString(_TL_('Summary bar')),
  }), [i18n]);

  return selectedParent && selectedChild ? (
    <div className={classes.summaryBar}>
      <SummaryBarContainer
        texts={ariaLabel}
        backButtonProps={backButtonProps}
        breadcrumb={null}
        summaryBar={summaryBar}
        showScopeBackButton={isNavigationRefreshOn(permissions) && !hideBackButton}
        i18n={i18n}
      />
      <Dialog
        open={dialogOpen}
        modalType="alert"
        onOpenChange={(event, data) => {
          setDialogOpen(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{i18n.getString(_TL_('Are you sure you want to exit?'))}</DialogTitle>
            <DialogContent>
              {i18n.getString(_TL_('If you exit now, your changes may not be saved.'))}
            </DialogContent>

            <DialogActions>
              <Button appearance="primary" onClick={onExit}>
                {i18n.getString(_TL_('Yes'))}
              </Button>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" onClick={onCancelDialog}>
                  {i18n.getString(_TL_('No'))}
                </Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  ) : null;
};

AdStudioSummaryBarInternal.propTypes = {
  classes: PropTypes.shape({
    adStudioSummaryBar: PropTypes.string,
    inProgressContent: PropTypes.string,
    summaryBar: PropTypes.string,
  }).isRequired,
  selectedParent: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  selectedChild: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      NavigationRefresh: PropTypes.bool,
    }),
  }).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
};

AdStudioSummaryBarInternal.defaultProps = {
};

export const AdStudioSummaryBar = withScope(withPermissions(withDefaultStyles(
  AdStudioSummaryBarInternal,
  getAdStudioSummaryBarStyle
)));
